import { Component, inject, signal, computed } from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ChatService, ChatConversation, ChatMessage } from '../../core/chat/chat.service';
import { Auth } from '../../core/auth/auth';
import { ThemeService } from '../../core/theme/theme.service';
import { WebSocketService } from '../../core/websocket/websocket.service';

@Component({
  selector: 'app-chat-bubble',
  standalone: true,
  imports: [MatIconModule, FormsModule],
  template: `
    <!-- Chat Bubble Toggle Button -->
    @if (!isChatOpen() && shouldShowChatBubble()) {
      <div class="chat-bubble-container">
        <button class="chat-bubble-btn" (click)="openChat()" [class.has-unread]="getTotalUnreadCount() > 0">
          <mat-icon>chat</mat-icon>
          @if (getTotalUnreadCount() > 0) {
            <span class="unread-badge">{{ getTotalUnreadCount() }}</span>
          }
        </button>
      </div>
    }
    
    <!-- Chat Panel -->
    @if (isChatOpen()) {
      <div class="chat-panel" [class.dark]="theme() === 'dark'">
        <!-- Conversation List View -->
        @if (!selectedConversation()) {
          <div class="conversations-view">
            <!-- Chat Header -->
            <div class="chat-header">
              <div class="chat-title">
                <mat-icon>chat</mat-icon>
                <span>Messages</span>
              </div>
              <div class="chat-actions">
                <button class="action-btn" (click)="showNewChatDialog()" title="Start new chat">
                  <mat-icon>add</mat-icon>
                </button>
                <button class="action-btn" (click)="goToMessages()" title="View all messages">
                  <mat-icon>open_in_new</mat-icon>
                </button>
                <button class="action-btn" (click)="closeChat()" title="Close chat">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>
            <div class="chat-search">
              <mat-icon>search</mat-icon>
              <input type="text" placeholder="Search chats" [(ngModel)]="searchQuery">
            </div>
            <!-- Recent Conversations -->
            <div class="conversations-list">
              @for (conversation of filteredConversations(); track conversation) {
                <div class="conversation-item"
                  (click)="selectConversation(conversation)"
                  [class.temporary]="conversation.id === 'temp'">
                  <div class="conversation-avatar">
                    <div class="avatar"
                      [class.has-image]="conversation.participant_avatar"
                      [style.background-image]="conversation.participant_avatar ? 'url(' + conversation.participant_avatar + ')' : null">
                      @if (!conversation.participant_avatar) {
                        <span class="avatar-initials">
                          {{ getInitials(getConversationTitle(conversation)) }}
                        </span>
                      }
                    </div>
                    @if (!conversation.is_group) {
                      <div class="status-dot" [class]="conversation.status"></div>
                    }
                  </div>
                  <div class="conversation-content">
                    <div class="conversation-header">
                      <span class="participant-name">{{ getConversationTitle(conversation) }}</span>
                      <span class="last-seen">{{ getLastSeenText(conversation.last_seen) }}</span>
                    </div>
                    <div class="last-message">
                      <span class="message-preview">{{ getLastMessagePreview(conversation) }}</span>
                      <span class="message-time">{{ getMessageTime(conversation.last_message?.timestamp) }}</span>
                    </div>
                  </div>
                  <div class="conversation-meta">
                    @if (conversation.unread_count > 0) {
                      <span class="unread-count">
                        {{ conversation.unread_count }}
                      </span>
                    }
                  </div>
                </div>
              }
              <!-- Empty state -->
              @if (filteredConversations().length === 0) {
                <div class="empty-state">
                  <mat-icon>chat_bubble_outline</mat-icon>
                  <p>No conversations found</p>
                  <span>Start a chat to see it here</span>
                </div>
              }
            </div>
          </div>
        }
        <!-- Chat View -->
        @if (selectedConversation()) {
          <div class="chat-view">
            <!-- Chat Header -->
            <div class="chat-header">
              <div class="chat-title">
                <button class="back-btn" (click)="backToConversations()" title="Back to conversations">
                  <mat-icon>arrow_back</mat-icon>
                </button>
                <div class="participant-info">
                  <div class="participant-avatar">
                    <div class="avatar"
                      [class.has-image]="selectedConversation()?.participant_avatar"
                      [style.background-image]="selectedConversation()?.participant_avatar ? 'url(' + selectedConversation()?.participant_avatar + ')' : null">
                      @if (!selectedConversation()?.participant_avatar) {
                        <span class="avatar-initials">
                          {{ getInitials(getConversationTitle(selectedConversation())) }}
                        </span>
                      }
                    </div>
                    @if (!selectedConversation()?.is_group) {
                      <div class="status-dot" [class]="selectedConversation()?.status"></div>
                    }
                  </div>
                  <div class="participant-details">
                    <span class="participant-name">{{ getConversationTitle(selectedConversation()) }}</span>
                    <span class="participant-status">{{ getConversationStatus(selectedConversation()) }}</span>
                    @if (getTypingIndicator()) {
                      <span class="typing-indicator">{{ getTypingIndicator() }}</span>
                    }
                  </div>
                </div>
              </div>
              <div class="chat-actions">
                <button class="action-btn" (click)="goToMessages()" title="View full conversation">
                  <mat-icon>open_in_new</mat-icon>
                </button>
                <button class="action-btn" (click)="closeChat()" title="Close chat">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>
            <!-- Messages Area -->
            <div class="messages-area">
              <div class="messages-list">
                @for (message of messages(); track message) {
                  <div class="message"
                    [class.my-message]="isMyMessage(message)"
                    [class.other-message]="!isMyMessage(message)"
                    [class.embed-message]="message.message_type === 'embed'">
                    @if (!isMyMessage(message)) {
                      <div class="message-avatar">
                        <div class="avatar"
                          [class.has-image]="selectedConversation()?.is_group ? message.sender_avatar : selectedConversation()?.participant_avatar"
                          [style.background-image]="(selectedConversation()?.is_group ? message.sender_avatar : selectedConversation()?.participant_avatar) ? 'url(' + (selectedConversation()?.is_group ? message.sender_avatar : selectedConversation()?.participant_avatar) + ')' : null">
                          @if (!(selectedConversation()?.is_group ? message.sender_avatar : selectedConversation()?.participant_avatar)) {
                            <span class="avatar-initials">
                              {{ getInitials(selectedConversation()?.is_group ? (message.sender_name || '') : (selectedConversation()?.participant_name || '')) }}
                            </span>
                          }
                        </div>
                      </div>
                    }
                    <div class="message-content">
                      <div class="message-bubble">
                        @if (selectedConversation()?.is_group && !isMyMessage(message)) {
                          <span class="sender-name">{{ message.sender_name }}</span>
                        }
                        @if (message.message_type !== 'embed') {
                          <span class="message-text">{{ message.content }}</span>
                        }
                        @if (message.message_type === 'embed') {
                          <span class="embed-card">
                            <span class="embed-pill">{{ message.embed?.entity_subtype || message.embed?.entity_type || message.embed?.type || 'item' }}</span>
                            <span class="embed-title">{{ message.embed?.title || message.content || 'Shared item' }}</span>
                            @if (message.embed?.subtitle) {
                              <span class="embed-subtitle">{{ message.embed?.subtitle }}</span>
                            }
                            <span class="embed-meta">{{ message.embed?.meta || message.embed?.entity_type || message.embed?.type || 'item' }}</span>
                          </span>
                        }
                        <span class="message-time">{{ getMessageTime(message.timestamp) }}</span>
                      </div>
                    </div>
                  </div>
                }
                <!-- Empty state -->
                @if (messages().length === 0) {
                  <div class="empty-messages">
                    <mat-icon>chat_bubble_outline</mat-icon>
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                }
              </div>
            </div>
            <!-- Message Input -->
            <div class="message-input-area">
              <div class="message-input-container">
                <input
                  type="text"
                  class="message-input"
                  placeholder="Type a message..."
                  [(ngModel)]="newMessage"
                  (input)="onTypingInput()"
                  (blur)="onTypingBlur()"
                  (keydown.enter)="sendMessage()"
                  #messageInput>
                <button class="send-btn" (click)="sendMessage()" [disabled]="!newMessage.trim()">
                  <mat-icon>send</mat-icon>
                </button>
              </div>
            </div>
          </div>
        }
      </div>
    }
    `,
  styles: `
    :host { display: block; }
    
    .chat-bubble-container {
      position: fixed;
      bottom: 24px;
      right: 24px;
      z-index: 1000;
    }
    
    .chat-bubble-btn {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: #3B82F6;
      border: none;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
      transition: all 0.2s ease;
      position: relative;
    }
    
    .chat-bubble-btn:hover {
      background: #2563EB;
      transform: scale(1.05);
      box-shadow: 0 6px 16px rgba(59, 130, 246, 0.5);
    }
    
    .chat-bubble-btn.has-unread {
      background: #EF4444;
      animation: pulse 2s infinite;
    }
    
    .chat-bubble-btn.has-unread:hover {
      background: #DC2626;
    }
    
    .unread-badge {
      position: absolute;
      top: -4px;
      right: -4px;
      background: #F59E0B;
      color: white;
      border-radius: 50%;
      width: 20px;
      height: 20px;
      font-size: 12px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid white;
    }
    
    @keyframes pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }
    
    .chat-panel {
      position: fixed;
      bottom: 24px;
      right: 24px;
      width: 360px;
      height: 540px;
      background: white;
      border-radius: 16px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      z-index: 1001;
      border: 1px solid #E5E7EB;
    }
    
    .chat-panel.dark {
      background: #1A202C;
      color: #E2E8F0;
    }
    
    .chat-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px;
      border-bottom: 1px solid #E5E7EB;
      background: #F9FAFB;
    }
    
    .chat-panel.dark .chat-header {
      background: #2D3748;
      border-bottom-color: #374151;
    }
    
    .chat-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 600;
      color: #1A202C;
    }
    
    .chat-panel.dark .chat-title {
      color: #E2E8F0;
    }
    
    .chat-actions {
      display: flex;
      gap: 4px;
    }
    
    .action-btn {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      border: none;
      background: transparent;
      color: #6B7280;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s ease;
    }
    
    .action-btn:hover {
      background: #E5E7EB;
      color: #374151;
    }
    
    .chat-panel.dark .action-btn {
      color: #94A3B8;
    }
    
    .chat-panel.dark .action-btn:hover {
      background: #374151;
      color: #E2E8F0;
    }
    
    .chat-search {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 16px;
      border-bottom: 1px solid #E5E7EB;
      background: #FFFFFF;
    }

    .chat-search mat-icon {
      font-size: 18px;
      width: 18px;
      height: 18px;
      color: #9CA3AF;
    }

    .chat-search input {
      flex: 1;
      border: none;
      outline: none;
      font-size: 13px;
      color: #1F2937;
      background: transparent;
    }

    .chat-panel.dark .chat-search {
      background: #1F2937;
      border-bottom-color: #374151;
    }

    .chat-panel.dark .chat-search input {
      color: #E5E7EB;
    }

    .conversations-list {
      flex: 1;
      overflow-y: auto;
      padding: 8px;
    }
    
    .conversation-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.2s ease;
      margin-bottom: 4px;
    }
    
    .conversation-item:hover {
      background: #F3F4F6;
    }
    
    .conversation-item.selected {
      background: #EBF4FF;
    }
    
    .conversation-item.temporary {
      border: 2px dashed #3B82F6;
      background: #F0F9FF;
    }
    
    .conversation-item.temporary:hover {
      background: #E0F2FE;
    }
    
    .chat-panel.dark .conversation-item:hover {
      background: #2D3748;
    }
    
    .chat-panel.dark .conversation-item.selected {
      background: #1E3A8A;
    }
    
    .chat-panel.dark .conversation-item.temporary {
      border-color: #60A5FA;
      background: #1E3A8A;
    }
    
    .chat-panel.dark .conversation-item.temporary:hover {
      background: #1E40AF;
    }
    
    .conversation-avatar {
      position: relative;
    }
    
    .avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      background: #6B7280;
      display: flex;
      align-items: center;
      justify-content: center;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
    }
    
    .avatar.has-image {
      background-color: transparent;
    }
    
    .avatar-initials {
      color: white;
      font-weight: 600;
      font-size: 14px;
    }
    
    .status-dot {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      border: 2px solid white;
    }
    
    .status-dot.online {
      background: #22C55E;
    }
    
    .status-dot.away {
      background: #F59E0B;
    }
    
    .status-dot.offline {
      background: #6B7280;
    }
    
    .status-dot.busy {
      background: #EF4444;
    }
    
    .conversation-content {
      flex: 1;
      min-width: 0;
    }
    
    .conversation-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 4px;
    }
    
    .participant-name {
      font-weight: 600;
      font-size: 14px;
      color: #1A202C;
    }
    
    .chat-panel.dark .participant-name {
      color: #E2E8F0;
    }
    
    .last-seen {
      font-size: 12px;
      color: #9CA3AF;
    }
    
    .last-message {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    
    .message-preview {
      font-size: 13px;
      color: #6B7280;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1;
    }
    
    .chat-panel.dark .message-preview {
      color: #94A3B8;
    }
    
    .message-time {
      font-size: 12px;
      color: #9CA3AF;
      margin-left: 8px;
    }
    
    .conversation-meta {
      display: flex;
      align-items: center;
    }
    
    .unread-count {
      background: #EF4444;
      color: white;
      border-radius: 50%;
      width: 20px;
      height: 20px;
      font-size: 12px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 200px;
      color: #9CA3AF;
      text-align: center;
    }
    
    .empty-state mat-icon {
      font-size: 48px;
      width: 48px;
      height: 48px;
      margin-bottom: 16px;
      opacity: 0.5;
    }
    
    .empty-state p {
      margin: 0;
      font-size: 14px;
    }

    .empty-state span {
      font-size: 12px;
      color: #CBD5F5;
    }

    /* Chat View Styles */
    .chat-view {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .back-btn {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      border: none;
      background: transparent;
      color: #6B7280;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s ease;
      margin-right: 8px;
    }

    .back-btn:hover {
      background: #E5E7EB;
      color: #374151;
    }

    .chat-panel.dark .back-btn {
      color: #94A3B8;
    }

    .chat-panel.dark .back-btn:hover {
      background: #374151;
      color: #E2E8F0;
    }

    .participant-info {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .participant-avatar {
      position: relative;
    }

    .participant-details {
      display: flex;
      flex-direction: column;
    }

    .participant-status {
      font-size: 12px;
      color: #9CA3AF;
    }

    .typing-indicator {
      font-size: 11px;
      color: #3B82F6;
    }

    .messages-area {
      flex: 1;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .messages-list {
      flex: 1;
      overflow-y: auto;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .message {
      display: flex;
      align-items: flex-end;
      gap: 8px;
    }

    .message.my-message {
      flex-direction: row-reverse;
    }

    .message-avatar {
      width: 32px;
      height: 32px;
    }

    .message-avatar .avatar {
      width: 32px;
      height: 32px;
      font-size: 12px;
    }

    .message-content {
      max-width: 70%;
    }

    .message-bubble {
      background: #F3F4F6;
      border-radius: 18px;
      padding: 12px 16px;
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .message.embed-message .message-bubble {
      background: transparent;
      padding: 0;
    }

    .my-message .message-bubble {
      background: #3B82F6;
      color: white;
    }

    .chat-panel.dark .message-bubble {
      background: #374151;
      color: #E2E8F0;
    }

    .chat-panel.dark .my-message .message-bubble {
      background: #1E40AF;
      color: white;
    }

    .message-text {
      font-size: 14px;
      line-height: 1.4;
      word-wrap: break-word;
    }

    .sender-name {
      font-size: 12px;
      font-weight: 600;
      opacity: 0.8;
    }

    .embed-card {
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding: 10px 12px;
      border-radius: 12px;
      border-left: 3px solid #3B82F6;
      background: rgba(59, 130, 246, 0.12);
      box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
      color: inherit;
    }

    .embed-pill {
      align-self: flex-start;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      padding: 2px 8px;
      border-radius: 999px;
      background: rgba(59, 130, 246, 0.16);
    }

    .embed-title {
      font-weight: 600;
      font-size: 13px;
    }

    .embed-subtitle {
      font-size: 12px;
      opacity: 0.75;
    }

    .embed-meta {
      font-size: 12px;
      opacity: 0.7;
    }


    .message-time {
      font-size: 11px;
      opacity: 0.7;
      align-self: flex-end;
    }

    .empty-messages {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 200px;
      color: #9CA3AF;
      text-align: center;
    }

    .empty-messages mat-icon {
      font-size: 48px;
      width: 48px;
      height: 48px;
      margin-bottom: 16px;
      opacity: 0.5;
    }

    .empty-messages p {
      margin: 0;
      font-size: 14px;
    }

    .message-input-area {
      padding: 16px;
      border-top: 1px solid #E5E7EB;
      background: #F9FAFB;
    }

    .chat-panel.dark .message-input-area {
      background: #2D3748;
      border-top-color: #374151;
    }

    .message-input-container {
      display: flex;
      align-items: center;
      gap: 8px;
      background: white;
      border-radius: 24px;
      padding: 8px 16px;
      border: 1px solid #E5E7EB;
    }

    .chat-panel.dark .message-input-container {
      background: #374151;
      border-color: #4B5563;
    }

    .message-input {
      flex: 1;
      border: none;
      outline: none;
      background: transparent;
      font-size: 14px;
      color: #1A202C;
      padding: 4px 0;
    }

    .chat-panel.dark .message-input {
      color: #E2E8F0;
    }

    .message-input::placeholder {
      color: #9CA3AF;
    }

    .send-btn {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: none;
      background: #3B82F6;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .send-btn:hover:not(:disabled) {
      background: #2563EB;
    }

    .send-btn:disabled {
      background: #9CA3AF;
      cursor: not-allowed;
    }
  `
})
export class ChatBubble {
  private chatService = inject(ChatService);
  private router = inject(Router);
  private auth = inject(Auth);
  private themeService = inject(ThemeService);
  private wsService = inject(WebSocketService);
  private typingStopTimeout: ReturnType<typeof setTimeout> | null = null;
  
  conversations = this.chatService.conversations;
  isChatOpen = this.chatService.isChatOpen;
  selectedConversation = this.chatService.selectedConversation;
  messages = this.chatService.messages;
  temporaryConversation = this.chatService.temporaryConversation;
  // Get the signals directly from the service
  chatBubbleEnabled = this.chatService.chatBubbleEnabled;
  isInMessagesApp = this.chatService.isInMessagesApp;
  
  shouldShowChatBubble = computed(() => {
    const result = this.chatBubbleEnabled() && !this.isInMessagesApp();
    console.log('ChatBubble: shouldShowChatBubble computed =', result, '(chatBubbleEnabled:', this.chatBubbleEnabled(), ', isInMessagesApp:', this.isInMessagesApp(), ')');
    return result;
  });
  theme = computed(() => this.themeService.theme());
  newMessage = '';
  searchQuery = '';

  // Get all conversations including temporary ones
  allConversations = computed(() => {
    const regular = this.conversations();
    const temp = this.temporaryConversation();
    const filtered = (regular || []).filter((conv: any) => conv && conv.id);
    return temp ? [temp, ...filtered] : filtered;
  });

  filteredConversations = computed(() => {
    const query = (this.searchQuery || '').toLowerCase().trim();
    if (!query) return this.allConversations();
    return this.allConversations().filter(conv => {
      const name = (conv.participant_name || conv.name || '').toLowerCase();
      const last = (conv.last_message?.content || '').toLowerCase();
      return name.includes(query) || last.includes(query);
    });
  });

  getTotalUnreadCount(): number {
    return this.chatService.getTotalUnreadCount();
  }

  openChat(): void {
    this.chatService.openChat();
  }

  closeChat(): void {
    this.chatService.closeChat();
  }

  selectConversation(conversation: ChatConversation): void {
    this.chatService.selectConversation(conversation);
  }

  goToMessages(): void {
    this.router.navigate(['/dashboard', 'messages']);
    this.closeChat();
  }

  showNewChatDialog(): void {
    // For now, navigate to messages page where users can start new conversations
    // In the future, this could open a modal to select a team member
    this.router.navigate(['/dashboard', 'messages']);
    this.closeChat();
  }

  getInitials(name: string): string {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  getLastSeenText(lastSeen?: Date): string {
    if (!lastSeen) return '';
    
    const now = new Date();
    const diff = now.getTime() - lastSeen.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  }

  getMessageTime(timestamp?: Date | null): string {
    if (!timestamp || !(timestamp instanceof Date) || isNaN(timestamp.getTime())) {
      return '';
    }
    
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return timestamp.toLocaleDateString();
  }

  getLastMessagePreview(conversation: ChatConversation): string {
    if (conversation.id === 'temp') {
      return 'Start typing to begin conversation...';
    }
    if (conversation.last_message?.message_type === 'embed') {
      const title = conversation.last_message.embed?.title || conversation.last_message.content || 'Shared item';
      return `Shared: ${title}`;
    }
    return conversation.last_message?.content || 'No messages yet';
  }

  backToConversations(): void {
    this.chatService.selectedConversation.set(null);
  }

  getConversationTitle(conversation: ChatConversation | null): string {
    if (!conversation) return '';
    if (conversation.is_group) {
      return conversation.name || conversation.participant_name || 'Group chat';
    }
    return conversation.participant_name;
  }

  getConversationStatus(conversation: ChatConversation | null): string {
    if (!conversation) return '';
    if (conversation.is_group) {
      return `${conversation.participant_count || 0} members`;
    }
    return this.getStatusText(conversation.status);
  }

  getStatusText(status?: string): string {
    switch (status) {
      case 'online': return 'Online';
      case 'away': return 'Away';
      case 'busy': return 'Busy';
      case 'offline': return 'Offline';
      default: return 'Offline';
    }
  }

  isMyMessage(message: ChatMessage): boolean {
    const selectedProfile = this.auth.getSelectedProfile();
    return selectedProfile ? message.sender_id === selectedProfile.id : false;
  }

  sendMessage(): void {
    if (!this.newMessage.trim() || !this.selectedConversation()) return;
    
    this.chatService.sendMessage(this.newMessage.trim(), this.selectedConversation()!.participant_id || '');
    this.clearTypingState();
    this.newMessage = '';
  }

  onTypingInput(): void {
    const profile = this.auth.getSelectedProfile();
    if (!profile || !this.selectedConversation()) {
      return;
    }
    this.wsService.sendTypingStatus(true, profile.display_name || 'Someone');
    if (this.typingStopTimeout) {
      clearTimeout(this.typingStopTimeout);
    }
    this.typingStopTimeout = setTimeout(() => {
      this.wsService.sendTypingStatus(false, profile.display_name || 'Someone');
      this.typingStopTimeout = null;
    }, 1200);
  }

  onTypingBlur(): void {
    this.clearTypingState();
  }

  getTypingIndicator(): string {
    if (!this.selectedConversation()) {
      return '';
    }
    const profile = this.auth.getSelectedProfile();
    const selfName = profile?.display_name || '';
    const typingUsers = Array.from(this.wsService.typingUsers()).filter(name => !!name && name !== selfName);
    if (!typingUsers.length) {
      return '';
    }
    return typingUsers.length === 1 ? `${typingUsers[0]} is typing...` : `${typingUsers.length} people are typing...`;
  }

  private clearTypingState(): void {
    const profile = this.auth.getSelectedProfile();
    if (profile) {
      this.wsService.sendTypingStatus(false, profile.display_name || 'Someone');
    }
    if (this.typingStopTimeout) {
      clearTimeout(this.typingStopTimeout);
      this.typingStopTimeout = null;
    }
  }
}
